import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import DeleteModal from "../components/utils/DeleteModal";
import FancyDialogActions from "../components/utils/FancyDialogActions";
import FancyTooltip from "../components/utils/FancyTooltip";
import { formatHours } from "../components/utils/formatters";
import { TrashIcon } from "../components/svgicons/SvgIcons";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FancyPaper from "../components/FancyPaper";
import FancyTablePagination from "../components/utils/FancyTablePagination";
import { useNavigate } from "react-router-dom";
import {
  AdministrativePerson,
  AdministrativeSalaryHistoryItem,
  AdministrativeSalaryTotals,
  deleteAdministrativeSalary,
  exportAdministrativeSalaryExcel,
  exportAdministrativeSalaryPdf,
  getAdministrativePersons,
  getAdministrativeSalaryHistoryPaged,
  getAdministrativeSalaryTotals,
} from "../services/administrativeSalary/AdministrativeSalaryService";

// =============================================================================
// Historial de Salarios Administrativos – vista solo lectura
// Acuerdos:
//   - Solo lectura (los meses cerrados no se editan)
//   - Filtros por mes, semana y búsqueda
//   - Selección múltiple de colaboradores con totales dinámicos
//   - Si no hay selección → totales del grupo completo filtrado
// =============================================================================

const MONTH_LABELS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const buildMonthOptions = () => {
  const today = new Date();
  const options: { value: string; label: string; year: number; month: number }[] = [];
  for (let i = 0; i < 24; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    options.push({
      value: `${y}-${String(m).padStart(2, "0")}`,
      label: `${MONTH_LABELS[m - 1]} ${y}`,
      year: y,
      month: m,
    });
  }
  return options;
};

// Misma lógica que ManagementSalaryCalculation: una semana pertenece al mes
// si su domingo cae dentro del mes (4+ días del mes en esa semana).
const countWeeksInMonth = (year: number, month: number): number => {
  const lastDay = new Date(year, month, 0).getDate();
  const firstDow = new Date(year, month - 1, 1).getDay();
  const daysToFirstSun = (7 - firstDow) % 7;
  let sunDate = 1 + daysToFirstSun;
  let count = 0;
  while (sunDate <= lastDay) { count++; sunDate += 7; }
  return count;
};

const formatCRC = (value: number) =>
  new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency: "CRC",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const ManagementSalaryHistory = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const monthOptions = useMemo(() => buildMonthOptions(), []);
  const defaultMonth = monthOptions[0]?.value ?? "";
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const [selectedWeek, setSelectedWeek] = useState("all");
  const [admins, setAdmins] = useState<AdministrativePerson[]>([]);
  const [selectedAdmin, setSelectedAdmin] = useState<AdministrativePerson | null>(null);
  const [rows, setRows] = useState<AdministrativeSalaryHistoryItem[]>([]);
  // Identidad por (personId, positionId) — una persona puede aparecer con varios cargos
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const pageSize = 20;
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [serverTotals, setServerTotals] = useState<AdministrativeSalaryTotals | null>(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [exporting, setExporting] = useState<"excel" | "pdf" | null>(null);
  const [rowToDelete, setRowToDelete] =
    useState<AdministrativeSalaryHistoryItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const weekParam = selectedWeek === "all" ? undefined : Number(selectedWeek);

  const monthLabel =
    monthOptions.find((m) => m.value === selectedMonth)?.label ?? "";

  // Opciones de semana según el mes seleccionado (las semanas reales del mes)
  const weekOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = [
      { value: "all", label: "Todas las semanas" },
    ];
    const monthOpt = monthOptions.find((m) => m.value === selectedMonth);
    if (!monthOpt) return opts;
    const count = countWeeksInMonth(monthOpt.year, monthOpt.month);
    for (let i = 1; i <= count; i++) {
      opts.push({ value: String(i), label: `Semana ${i}` });
    }
    return opts;
  }, [selectedMonth, monthOptions]);

  // Si la semana seleccionada ya no existe en el nuevo mes, resetear a "all"
  useEffect(() => {
    if (selectedWeek === "all") return;
    if (!weekOptions.some((w) => w.value === selectedWeek)) {
      setSelectedWeek("all");
    }
  }, [weekOptions, selectedWeek]);

  const loadData = useCallback(async (targetPage: number) => {
    const selected = monthOptions.find((m) => m.value === selectedMonth);
    if (!selected) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const filterArg = selectedAdmin?.fullName?.trim() || undefined;
      const [paged, totals] = await Promise.all([
        getAdministrativeSalaryHistoryPaged(
          selected.year,
          selected.month,
          targetPage,
          pageSize,
          weekParam,
          filterArg
        ),
        getAdministrativeSalaryTotals(
          selected.year,
          selected.month,
          weekParam,
          filterArg
        ),
      ]);
      setRows(paged.content);
      setTotalElements(paged.totalElements);
      setTotalPages(paged.totalPages);
      setServerTotals(totals);
    } catch (err: any) {
      setRows([]);
      setTotalElements(0);
      setTotalPages(0);
      setServerTotals(null);
      setErrorMsg(
        err?.message ?? "No se pudo cargar el historial. Intente de nuevo."
      );
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, weekParam, selectedAdmin, monthOptions]);

  // Cargar lista de colaboradores administrativos al montar (para el Autocomplete)
  useEffect(() => {
    (async () => {
      try {
        const list = await getAdministrativePersons();
        setAdmins(list);
      } catch (err: any) {
        setErrorMsg(err?.message ?? "No se pudo cargar la lista de colaboradores");
      }
    })();
  }, []);

  // Recargar al cambiar mes/semana/colaborador
  useEffect(() => {
    setPage(0);
    setSelectedKeys(new Set());
    loadData(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedWeek, selectedAdmin]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    const zeroBased = newPage - 1;
    setPage(zeroBased);
    loadData(zeroBased);
  };

  const rowKey = (r: AdministrativeSalaryHistoryItem) =>
    `${r.personId}-${r.positionId}`;

  const handleOpenDelete = (
    r: AdministrativeSalaryHistoryItem,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setRowToDelete(r);
  };

  const handleConfirmDelete = async () => {
    if (!rowToDelete) return;
    const selected = monthOptions.find((m) => m.value === selectedMonth);
    if (!selected) return;
    setDeleting(true);
    setErrorMsg(null);
    try {
      await deleteAdministrativeSalary(
        rowToDelete.personId,
        rowToDelete.positionId,
        selected.year,
        selected.month
      );
      setSuccessMsg(
        `Registro de ${rowToDelete.personName} eliminado correctamente`
      );
      setRowToDelete(null);
      // Quitar de la selección si estaba marcado
      setSelectedKeys((prev) => {
        const next = new Set(prev);
        next.delete(rowKey(rowToDelete));
        return next;
      });
      await loadData(page);
    } catch (err: any) {
      setErrorMsg(err?.message ?? "No se pudo eliminar el registro");
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async (kind: "excel" | "pdf") => {
    const selected = monthOptions.find((m) => m.value === selectedMonth);
    if (!selected) return;
    setExporting(kind);
    setErrorMsg(null);
    try {
      // El export filtra por personId (no granularidad de cargo). Deduplicar.
      const personIds = Array.from(
        new Set(
          rows
            .filter((r) => selectedKeys.has(rowKey(r)))
            .map((r) => r.personId)
        )
      );
      const args = [
        selected.year,
        selected.month,
        weekParam,
        selectedAdmin?.fullName?.trim() || undefined,
        personIds.length > 0 ? personIds : undefined,
      ] as const;
      if (kind === "excel") {
        await exportAdministrativeSalaryExcel(...args);
      } else {
        await exportAdministrativeSalaryPdf(...args);
      }
    } catch (err: any) {
      setErrorMsg(err?.message ?? "No se pudo exportar el archivo");
    } finally {
      setExporting(null);
    }
  };

  // Toggle individual
  const toggleRow = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Toggle todos (select all / clear all)
  const allSelected =
    rows.length > 0 && rows.every((r) => selectedKeys.has(rowKey(r)));
  const someSelected = !allSelected && selectedKeys.size > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(rows.map(rowKey)));
    }
  };

  // Totales del mes filtrado completo (vienen del backend, no del subset paginado).
  const totals = {
    collaborators: serverTotals?.totalCollaborators ?? 0,
    bruto: serverTotals?.totalGross ?? 0,
    ded: serverTotals?.totalDeductions ?? 0,
    neto: serverTotals?.totalNet ?? 0,
  };

  return (
    <FancyPaper
      pagetitle="Historial de Salarios Administrativos"
      prevpage="Salarios administrativos"
    >
      {/* Cabecera con botón Volver */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Historial de salarios administrativos
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Consulta los registros de meses anteriores en modo solo lectura.
            Selecciona colaboradores para filtrarlos en la exportación.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Volver
        </Button>
      </Stack>

      {/* Filtros */}
      <Paper
        sx={{
          background: theme.palette.secondary.light,
          borderRadius: "10px",
          p: 3,
          mb: 3,
        }}
        elevation={0}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Mes"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ background: "#FFF", borderRadius: 1 }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: { maxHeight: 280 },
                  },
                },
              }}
            >
              {monthOptions.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Semana"
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ background: "#FFF", borderRadius: 1 }}
            >
              {weekOptions.map((w) => (
                <MenuItem key={w.value} value={w.value}>
                  {w.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Autocomplete
              fullWidth
              size="small"
              options={admins}
              getOptionLabel={(opt) => opt.fullName}
              value={selectedAdmin}
              onChange={(_, v) => setSelectedAdmin(v)}
              isOptionEqualToValue={(opt, val) => opt.personId === val.personId}
              noOptionsText="Sin resultados"
              sx={{ background: "#FFF", borderRadius: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Colaborador"
                  size="small"
                  placeholder="Todos los colaboradores"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      {errorMsg && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMsg(null)}>
          {errorMsg}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}

      {/* Resumen dinámico (total general o del grupo seleccionado) */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ mb: 3 }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
      >
        <Box>
          {selectedKeys.size > 0 ? (
            <Chip
              label={`Selección para exportar: ${selectedKeys.size}`}
              color="warning"
              variant="outlined"
              onDelete={() => setSelectedKeys(new Set())}
              sx={{ fontWeight: 600 }}
            />
          ) : (
            <Chip
              label="Totales del mes filtrado"
              color="default"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="flex-end"
        >
          <Chip
            label={`Colaboradores: ${totals.collaborators}`}
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={`Bruto: ${formatCRC(totals.bruto)}`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={`Deducciones: ${formatCRC(totals.ded)}`}
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
          <Chip
            label={`Neto: ${formatCRC(totals.neto)}`}
            color="success"
            sx={{ fontWeight: 600, color: "#FFF" }}
          />
        </Stack>
      </Stack>

      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="Historial de salarios">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  indeterminate={someSelected}
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={rows.length === 0}
                  inputProps={{
                    "aria-label": "Seleccionar todos los colaboradores",
                  }}
                />
              </TableCell>
              <TableCell>Colaborador</TableCell>
              <TableCell>Cargo</TableCell>
              <TableCell align="center">Salario base</TableCell>
              <TableCell align="center">Horas base</TableCell>
              <TableCell align="center">Horas extras</TableCell>
              <TableCell align="center">Horas vacaciones</TableCell>
              <TableCell align="center">Horas deducciones</TableCell>
              <TableCell align="center">Días trabajados</TableCell>
              <TableCell align="center">Salario bruto</TableCell>
              <TableCell align="center">Monto deducciones</TableCell>
              <TableCell align="center">Salario neto</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={13} align="center">
                  No hay registros para los filtros seleccionados.
                </TableCell>
              </TableRow>
            ) : (
              <>
                {rows.map((r) => {
                  const key = rowKey(r);
                  const isSelected = selectedKeys.has(key);
                  return (
                    <TableRow
                      key={key}
                      hover
                      selected={isSelected}
                      onClick={() => toggleRow(key)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isSelected}
                          onChange={() => toggleRow(key)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell scope="row">{r.personName}</TableCell>
                      <TableCell>{r.positionName}</TableCell>
                      <TableCell align="center">
                        {formatCRC(r.baseSalary)}
                      </TableCell>
                      <TableCell align="center">{formatHours(r.baseHours)}</TableCell>
                      <TableCell align="center">{formatHours(r.extraHours)}</TableCell>
                      <TableCell align="center">{formatHours(r.vacationHours)}</TableCell>
                      <TableCell align="center">{formatHours(r.deductionHours)}</TableCell>
                      <TableCell align="center">{r.workedDays}</TableCell>
                      <TableCell align="center">
                        {formatCRC(r.grossSalary)}
                      </TableCell>
                      <TableCell align="center">
                        {formatCRC(r.deductionsAmount)}
                      </TableCell>
                      <TableCell align="center">
                        {formatCRC(r.netSalary)}
                      </TableCell>
                      <TableCell align="center">
                        <FancyTooltip title="Eliminar" placement="top">
                          <IconButton
                            aria-label="trash"
                            color="error"
                            sx={{
                              "&:hover": { color: theme.palette.error.dark },
                            }}
                            onClick={(e) => handleOpenDelete(r, e)}
                          >
                            <TrashIcon />
                          </IconButton>
                        </FancyTooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}

                <TableRow>
                  <TableCell />
                  <TableCell sx={{ fontWeight: 700 }}>
                    {`TOTAL DEL MES (${totals.collaborators})`}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {formatCRC(totals.bruto)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {formatCRC(totals.ded)}
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    {formatCRC(totals.neto)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <FancyTablePagination
        count={rows.length}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={handleChangePage}
        totalElements={totalElements}
        totalPages={totalPages}
      />

      {/* Acciones de exportación */}
      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ mt: 3, mb: 2 }}
      >
        <Button
          variant="outlined"
          color="success"
          startIcon={<DownloadIcon />}
          sx={{ textTransform: "none" }}
          onClick={() => handleExport("excel")}
          disabled={exporting !== null || rows.length === 0}
        >
          {exporting === "excel" ? "Generando…" : "Exportar Excel"}
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<PictureAsPdfIcon />}
          sx={{ textTransform: "none" }}
          onClick={() => handleExport("pdf")}
          disabled={exporting !== null || rows.length === 0}
        >
          {exporting === "pdf" ? "Generando…" : "Exportar PDF"}
        </Button>
      </Stack>

      <Dialog
        open={rowToDelete !== null}
        onClose={() => !deleting && setRowToDelete(null)}
        fullWidth
        maxWidth="xs"
      >
        <DeleteModal
          textChildren={
            rowToDelete
              ? `${rowToDelete.personName} — ${rowToDelete.positionName} (${monthLabel}). Esta acción es permanente y no se puede deshacer.`
              : ""
          }
          actionButton={
            <FancyDialogActions
              handleAccept={handleConfirmDelete}
              cancelModal={() => setRowToDelete(null)}
              textAcceptButton={deleting ? "Eliminando…" : "Eliminar"}
            />
          }
        />
      </Dialog>
    </FancyPaper>
  );
};

export default ManagementSalaryHistory;
